import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import { saveAs } from "file-saver";
import { Button } from "semantic-ui-react";
import { useLanguage } from "../../../../contexts";
import { P, motor, textoSoloLectura, useEnergia, useGuardarSeccion, useModo, useSoloLectura } from "../engine";
import { ConInstalacion, Notice, SaveErrors, Section } from "../common/SectionCommon";
import "../common/common.scss";
import "./InformeReport.scss";

// Gráficos del informe como imagen (hoja 01 filas 100, 152): Chart.js sobre un canvas fuera de pantalla, igual que
// graficoImg del prototipo. Sin canvas (pruebas) el motor escribe «Gráfico no disponible en este entorno».
motor.fijarImagenGrafico((cfg) => {
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-12000px;top:0;width:860px;height:360px;";
  const cv = document.createElement("canvas");
  cv.width = 860;
  cv.height = 360;
  host.appendChild(cv);
  document.body.appendChild(host);
  try {
    const ctx = cv.getContext && cv.getContext("2d");
    if (!ctx) return "";
    const ch = new Chart(ctx, { ...cfg, options: { ...cfg.options, responsive: false, animation: false, devicePixelRatio: 2 } });
    const src = ch.toBase64Image();
    ch.destroy();
    return src;
  } finally {
    host.remove();
  }
});

// Las conclusiones se editan como texto plano. Al leer lo guardado, el motor (san) cambia < y > por ‹ ›: si se
// guardaran etiquetas, el informe mostraría ‹strong› como texto. Por eso se quitan al editar y < > & se escapan al
// guardar. Solo cuenta como etiqueta un «<» seguido de una letra: los textos traen comparaciones como «p <0,001».
const aEditor = (x) => String(x || "").replace(/‹/g, "<").replace(/›/g, ">").replace(/<\/?[a-z][^>]*>/gi, "")
  .replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
const aGuardar = (x) => String(x || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Conclusiones de la sección 12, editables (hoja 01 fila 75): se guardan solo si la planta las cambia; «Regenerar»
// vuelve a las automáticas (calculadas con los datos).
function EditorConclusiones({ f, installation, onSaved, t }) {
  const editadas = !!f._conclEdit;
  const inicial = () => P.conclusionesActivas(f).map((c) => ({ t: c.t, x: aEditor(c.x) }));
  const [lista, setLista] = useState(inicial);
  const [dirty, setDirty] = useState(false);
  const { guardar, saving, errores } = useGuardarSeccion(installation, onSaved, { t });
  // sin permiso, el editor se ve deshabilitado con el motivo (hoja 23 p. 66)
  const sinPermiso = useSoloLectura() === "permiso";
  useEffect(() => {
    setLista(P.conclusionesActivas(f).map((c) => ({ t: c.t, x: aEditor(c.x) })));
    setDirty(false);
  }, [f]);

  const cambiar = (i, k, v) => { setLista((l) => l.map((c, j) => (j === i ? { ...c, [k]: v } : c))); setDirty(true); };
  const quitar = (i) => { setLista((l) => l.filter((_, j) => j !== i)); setDirty(true); };
  const agregar = () => { setLista((l) => [...l, { t: t("energy_performance_concl_new_title"), x: "" }]); setDirty(true); };

  return (
    <Section title={t("energy_performance_concl_title")} note={t("energy_performance_concl_note")}>
      {sinPermiso && <p className="ep-section__note">{textoSoloLectura(t, "permiso")}</p>}
      <fieldset className="ep-concl__campos" disabled={sinPermiso}>
      <p className="ep-concl__estado">{editadas ? t("energy_performance_concl_edited") : t("energy_performance_concl_auto")}</p>
      {lista.length === 0 && <p className="ep-muted">{t("energy_performance_concl_empty")}</p>}
      {lista.map((c, i) => (
        <div className="ep-concl" key={i}>
          <div className="ep-concl__cab">
            <input className="ep-concl__t" aria-label={`${t("energy_performance_concl_heading")} ${i + 1}`} value={c.t} onChange={(e) => cambiar(i, "t", e.target.value)} />
            <Button type="button" basic size="small" onClick={() => quitar(i)}>{t("energy_performance_concl_remove")}</Button>
          </div>
          <textarea className="ep-concl__x" rows={4} aria-label={`${t("energy_performance_concl_text")} ${i + 1}`} value={c.x} onChange={(e) => cambiar(i, "x", e.target.value)} />
        </div>
      ))}
      <SaveErrors errores={errores} t={t} />
      <div className="ep-concl__acciones">
        <Button type="button" basic size="small" onClick={agregar}>{t("energy_performance_concl_add")}</Button>
        <Button type="button" basic size="small" disabled={saving || (!editadas && !dirty)} onClick={() => guardar({ conclusiones: [] })}>
          {t("energy_performance_concl_regenerate")}
        </Button>
        <Button type="button" primary size="small" loading={saving} disabled={saving || !dirty} onClick={() => guardar({ conclusiones: lista.map((c) => ({ t: c.t, x: aGuardar(c.x), editada: true })) })}>
          {t("energy_performance_concl_save")}
        </Button>
      </div>
      </fieldset>
    </Section>
  );
}

// Centro de descargas (hoja 01 fila 149): 11 archivos armados por el motor, sin «undefined».
function CentroDescargas({ f, modo, imprimir, t }) {
  const [estado, setEstado] = useState({});
  const marcar = (clave, v) => {
    setEstado((s) => ({ ...s, [clave]: v }));
    setTimeout(() => setEstado((s) => ({ ...s, [clave]: null })), 2200);
  };
  const bajar = (d) => {
    try {
      if (d.imprimir) imprimir();
      else {
        const a = motor.archivoDescarga(f, d.clave, { modo });
        saveAs(new Blob([a.datos], { type: a.mime }), a.nombre);
      }
      marcar(d.clave, "ok");
    } catch (e) {
      console.error(e);
      marcar(d.clave, "error");
    }
  };
  // títulos y descripciones traducidos; si falta la clave, el texto del motor
  const txt = (d, k) => { const key = `energy_performance_dl_${d.clave}_${k}`; const v = t(key); return v === key ? d[k] : v; };
  return (
    <Section title={t("energy_performance_dl_title")} note={t("energy_performance_dl_note")}>
      <div className="ep-descargas">
        {motor.DESCARGAS.map((d) => (
          <div className="ep-descarga" key={d.clave} data-descarga={d.clave}>
            <strong>{txt(d, "t")}</strong>
            <span>{txt(d, "d")}</span>
            <Button type="button" size="small" primary={d.clave === "informe" || d.clave === "excel"} basic={!(d.clave === "informe" || d.clave === "excel")} onClick={() => bajar(d)}>
              {estado[d.clave] === "ok" ? t("energy_performance_dl_done") : estado[d.clave] === "error" ? t("energy_performance_dl_error") : t("energy_performance_dl_button")}
            </Button>
          </div>
        ))}
      </div>
    </Section>
  );
}

// Informe A/B completo (hoja 28): lo arma el motor (generateReport del prototipo + conclusión de cada sección) y se
// muestra en un iframe aislado, que es además lo único que se imprime. Cambiar el uso lo regenera (fila 128).
export function InformeReport({ installation, onSaved }) {
  const { translations: tr } = useLanguage();
  const t = (key) => tr[key] || key;
  const modo = useModo();
  const vista = useEnergia(installation);
  const sinPermiso = useSoloLectura() === "permiso";
  const { guardar, saving } = useGuardarSeccion(installation, onSaved, { t });
  const [html, setHtml] = useState(null);
  const marco = useRef(null);
  const f = vista.f;

  // Se arma después de pintar la pantalla: con todos los gráficos como imagen tarda un momento.
  useEffect(() => {
    if (!f) return undefined;
    setHtml(null);
    const id = setTimeout(() => {
      try { setHtml(motor.informeHtml(f, { modo })); } catch (e) { console.error(e); setHtml(""); }
    }, 0);
    return () => clearTimeout(id);
  }, [f, modo]);

  const ajustarAlto = () => {
    const doc = marco.current && marco.current.contentDocument;
    if (doc && doc.body) marco.current.style.height = `${doc.documentElement.scrollHeight + 24}px`;
  };
  const imprimir = () => {
    const w = marco.current && marco.current.contentWindow;
    if (!w) throw new Error("informe no disponible");
    w.focus();
    w.print();
  };

  return (
    <ConInstalacion installation={installation} vista={vista} t={t}>
      {() => (
        <div className="ep-screen ep-informe">
          <h3 className="ep-screen__title">{t(modo === "B" ? "energy_performance_report_title_b" : "energy_performance_report_title_a")}</h3>
          <p className="ep-screen__intro">{t("energy_performance_report_intro")}</p>

          <div className="ep-informe__barra">
            <label className="ep-informe__opcion">
              {t("energy_performance_report_charts")}
              <select
                value={f.informeGraficos || "todos"}
                disabled={saving || sinPermiso}
                title={sinPermiso ? textoSoloLectura(t, "permiso") : undefined}
                onChange={(e) => guardar({ informeGraficos: e.target.value }, { silencioso: true })}
              >
                <option value="todos">{t("energy_performance_report_charts_all")}</option>
                <option value="elegidos">{t("energy_performance_report_charts_chosen")}</option>
              </select>
            </label>
            <Button type="button" primary size="small" disabled={!html} onClick={() => { try { imprimir(); } catch (e) { console.error(e); } }}>{t("energy_performance_report_print")}</Button>
          </div>

          <EditorConclusiones f={f} installation={installation} onSaved={onSaved} t={t} />
          <CentroDescargas f={f} modo={modo} imprimir={imprimir} t={t} />

          {html === null && <Notice tipo="info">{t("energy_performance_report_generating")}</Notice>}
          {html === "" && <Notice tipo="danger">{t("energy_performance_report_error")}</Notice>}
          {html && (
            <iframe
              ref={marco}
              className="ep-informe__marco"
              title={t(modo === "B" ? "energy_performance_report_title_b" : "energy_performance_report_title_a")}
              sandbox="allow-same-origin allow-modals"
              srcDoc={html}
              onLoad={ajustarAlto}
            />
          )}
        </div>
      )}
    </ConInstalacion>
  );
}
